import {
  ContentType,
  EntityType,
  type UploadMediaCommandInput,
  type UploadMediaCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import {
  extractBase64Files,
  replaceImagesWithMediaIds,
  extractMediaIdSrcValues,
} from 'src/utils/mediaUtils';

/**
 * MIME string → ContentType enum for inline images. Only image types
 * that can appear as base64 data URLs from pasting/uploading.
 */
const MIME_TO_CONTENT_TYPE = new Map<string, ContentType>([
  ['image/jpeg', ContentType.IMAGE_JPEG],
  ['image/png', ContentType.IMAGE_PNG],
  ['image/gif', ContentType.IMAGE_GIF],
  ['image/webp', ContentType.IMAGE_WEBP],
]);

/** Converts a base64 data URL to a Uint8Array of raw bytes. */
function base64DataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.split(',')[1] ?? '';
  const binary = atob(base64);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export interface UploadDescriptionImagesContext {
  programId: string;
  businessUnit: string;
  createdBy: string;
}

/**
 * Optional overrides so this helper serves both the task flow (its original
 * caller) and the question flow:
 * - `entityType` stamps the uploaded media's owning entity. Defaults to
 *   {@link EntityType.TASK} so existing task callers are unchanged; the
 *   question-publish flow passes {@link EntityType.QUESTION}.
 * - `fieldKey` is the key the returned media-id map is keyed under. Defaults to
 *   `'taskDescription'` (the task caller's single field); the question flow
 *   passes the specific criteria field (`questionText`, `passCriteria`, …).
 */
export interface UploadDescriptionImagesOptions {
  readonly entityType?: EntityType;
  readonly fieldKey?: string;
}

const DEFAULT_ENTITY_TYPE = EntityType.TASK;
const DEFAULT_FIELD_KEY = 'taskDescription';

export interface UploadDescriptionImagesResult {
  /** The cleaned HTML with base64 data URLs replaced by media IDs. */
  description: string;
  /** Map of field → mediaId[] for the API payload, keyed by the caller's
   *  `fieldKey` (defaults to `taskDescription`). */
  taskMediaIdsMap: Record<string, string[]>;
}

/** Builds the `{ [fieldKey]: mediaId[] }` payload map from the final HTML.
 *  Deduplicated — the same media ID can appear in multiple `<img>`
 *  tags but belongs in the payload once. Omits the key entirely when there
 *  are no media ids (so an empty field doesn't emit `{ field: [] }`). */
function buildFieldMediaIdsMap(html: string, fieldKey: string): Record<string, string[]> {
  const mediaIds = Array.from(new Set(extractMediaIdSrcValues(html)));
  return mediaIds.length > 0 ? { [fieldKey]: mediaIds } : {};
}

/**
 * Extracts inline base64 images from an HTML description, uploads each
 * to the Helios media service, and replaces the data URLs with the
 * returned media IDs. Returns the cleaned description and a
 * `taskMediaIdsMap` for the save payload.
 *
 * If the description has no base64 images, returns it unchanged with
 * an empty media map (no network calls).
 *
 * @param description - HTML content potentially containing base64 `<img>` tags
 * @param context - Identity fields for the upload request
 * @param uploadMedia - The mutation function from `useUploadMediaMutation`
 * @throws When an image has an unsupported MIME type or an upload does
 *   not return a media ID. Persisting raw base64 would silently bloat
 *   the payload and render a broken image, so the save must not proceed.
 */
export async function uploadDescriptionImages(
  description: string,
  context: UploadDescriptionImagesContext,
  uploadMedia: (input: UploadMediaCommandInput) => Promise<UploadMediaCommandOutput>,
  options: UploadDescriptionImagesOptions = {},
): Promise<UploadDescriptionImagesResult> {
  const entityType = options.entityType ?? DEFAULT_ENTITY_TYPE;
  const fieldKey = options.fieldKey ?? DEFAULT_FIELD_KEY;

  // Dedupe identical data URLs — extractBase64Files returns one entry
  // per occurrence, but the same pasted image only needs one upload;
  // replaceAll rewrites every occurrence to the same media ID.
  const images = Array.from(
    new Map(
      extractBase64Files(description).map((image) => [image.image, image]),
    ).values(),
  );

  if (images.length === 0) {
    // No images to upload — extract any existing media IDs for the map
    return {
      description,
      taskMediaIdsMap: buildFieldMediaIdsMap(description, fieldKey),
    };
  }

  // Reject unsupported MIME types up front. Silently skipping them
  // would leave raw base64 data URLs in the persisted description.
  const unsupported = images.filter(
    (image) => !MIME_TO_CONTENT_TYPE.has(image.extension),
  );
  if (unsupported.length > 0) {
    const types = Array.from(new Set(unsupported.map((image) => image.extension)));
    throw new Error(`Unsupported image type: ${types.join(', ')}`);
  }

  // Upload each base64 image; every upload must yield a media ID.
  const uploadResults = await Promise.all(
    images.map(async ({ image: dataUrl, extension }) => {
      const contentType = MIME_TO_CONTENT_TYPE.get(extension) as ContentType;
      const input: UploadMediaCommandInput = {
        programId: context.programId,
        businessUnit: context.businessUnit,
        entityType,
        createdBy: context.createdBy,
        file: base64DataUrlToBytes(dataUrl),
        contentType,
      };
      const result = await uploadMedia(input);
      const mediaId = result.media?.mediaId;
      if (!mediaId) {
        throw new Error('Image upload did not return a media ID');
      }
      return [dataUrl, mediaId] as const;
    }),
  );

  // Replace base64 data URLs with media IDs in the HTML.
  const cleanedDescription = replaceImagesWithMediaIds(
    description,
    Object.fromEntries(uploadResults),
  );

  // replaceImagesWithMediaIds swallows internal errors and returns the
  // ORIGINAL content on failure — never trust the rewrite blindly. If
  // any base64 payload survived, persisting it would silently defeat
  // this module's guarantee, so fail the save instead.
  if (extractBase64Files(cleanedDescription).length > 0) {
    throw new Error('Failed to replace inline images with media IDs');
  }

  // Build the payload map from the authoritative upload results plus
  // any media IDs already present in the original HTML (e.g. images
  // kept from a previously saved draft) — not by re-parsing the
  // rewritten HTML, which couples correctness to the rewrite step.
  const mediaIds = Array.from(
    new Set([
      ...extractMediaIdSrcValues(description),
      ...uploadResults.map(([, mediaId]) => mediaId),
    ]),
  );

  return {
    description: cleanedDescription,
    taskMediaIdsMap: { [fieldKey]: mediaIds },
  };
}
